import { BuildStatus, isFinalBuildStatus } from '@cadstrom/cadstrom-shared';

import type { useTRPC } from '#src/trpc.js';

/**
 * Cadence for polling a revision that has not settled into a terminal status. Slow enough that a
 * long-lived page does not hammer the revision API, while still picking up a transition driven by
 * another user, session, or worker.
 */
const NON_TERMINAL_REVISION_POLL_MS = 60_000;

/** Cadence for polling a revision whose BOM review was just submitted, so the page moves on soon after the merge. */
const MERGING_REVISION_POLL_MS = 10_000;

/**
 * Builds the polling query options for a revision.
 *
 * Built from the revision id rather than read back from router context, so a component that
 * renders while the route context is being rebuilt still gets a complete query definition.
 *
 * @param trpc - The tRPC options proxy.
 * @param revisionId - The revision to read.
 * @param options.reviewSubmitted - Whether the viewer just submitted the revision's BOM review; polls faster
 *   until the merge of that review finishes.
 * @returns Query options that poll until the revision reaches a status with no further transition.
 */
export function revisionQueryOptions(
	trpc: ReturnType<typeof useTRPC>,
	revisionId: string,
	options: { reviewSubmitted?: boolean } = {},
) {
	return trpc.revision.get.queryOptions(
		{ id: revisionId },
		{
			// Polling stops on the terminal statuses and on 'unknown', which carries no expectation
			// of a further transition.
			refetchInterval: (query) => {
				const status = query.state.data?.revision.status;
				if (status === undefined) {
					return query.state.error === null ? NON_TERMINAL_REVISION_POLL_MS : false;
				}
				if (status === BuildStatus.Unknown || isFinalBuildStatus(status)) {
					return false;
				}
				const isMerging = status === BuildStatus.BomAwaitingReview || status === BuildStatus.ProcessingBom;
				return options.reviewSubmitted === true && isMerging ? MERGING_REVISION_POLL_MS : NON_TERMINAL_REVISION_POLL_MS;
			},
		},
	);
}
